import Grid from '@material-ui/core/Grid'
import type { DropdownSelectOption } from '@paintscout/ui'
import {
  DateRangePicker,
  DropdownSelect,
  EstimatorSelect,
  KeypadInputField,
  TagSelection,
  useClientOptions,
  useUserPermissions
} from '@paintscout/ui'
import { getDealStageOptions, getObjectLabels, getQuoteTypeOptions, getTags } from '@paintscout/util/builder'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util/users'
import type { InFilter, RangeFilter, ValueFilter } from 'json-to-lucene'
import React from 'react'
import moment from 'moment'
import { handleFilterChange, handleRangeFilterChange } from '@paintscout/util/util'
import type { QuoteTag } from 'paintscout'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface DealSearchFiltersProps {
  invoice: boolean
  filters: ValueFilter[]
  hasStage: boolean
  pipeline: string
  onChange?: (filters: ValueFilter[]) => any
  sort?: string
  onChangeSort?: (sort: string) => any
  sortOptions?: DropdownSelectOption[]
  view: 'list' | 'board'
}

export default function DealSearchFilters({
  invoice,
  filters,
  hasStage,
  pipeline,
  onChange,
  sort,
  onChangeSort,
  sortOptions,
  view
}: DealSearchFiltersProps) {
  const { options } = useClientOptions()
  const { permissionToViewEstimator } = useUserPermissions()
  const { quote: quoteLabel, currency } = getObjectLabels({ options })
  const { canDeactivateSettingsItems } = useFlags()

  const archivedOptions: DropdownSelectOption[] = [
    { label: 'Include Archived', value: ['true', 'false'] },
    { label: 'Only Archived', value: ['true'] }
  ]

  const typeOptions = getQuoteTypeOptions({ options, hideAll: false, canDeactivate: !!canDeactivateSettingsItems }).map(
    (type) => ({
      value: type.key,
      label: type.label
    })
  )

  const stageOptions = getDealStageOptions({ options, pipeline }).map((stage) => ({
    value: stage.key,
    label: stage.label
  }))

  const filteredTags = getTags({
    options,
    keys: filters
      .filter((f) => f.id.startsWith('tags'))
      .map((f) => f.value)
      .flat()
  })

  const handleTagsChange = (newTags: QuoteTag[]) => {
    const tagFilter: ValueFilter = newTags.reduce(
      (acc, tag) => {
        return {
          ...acc,
          value: [...acc.value, tag.name]
        }
      },
      {
        id: `tags`,
        type: 'in',
        value: [],
        operator: 'AND'
      }
    )

    if (onChange) {
      onChange([...filters.filter(({ id }) => id !== 'tags'), tagFilter])
    }
  }

  const handleSort = (args: { name?: string; swapDirection?: boolean }) => {
    const { name, swapDirection } = args
    const isDesc = sort?.startsWith('-')
    const currentName = sort?.replace('-', '')
    const newName = name || currentName
    const newDirection = swapDirection ? (isDesc ? '' : '-') : isDesc ? '-' : ''
    const newSort = `${newDirection}${newName}`
    onChangeSort(newSort)
  }

  const dateFilter = filters.find((filter) => filter.id === 'created')
  const ownerFilter = filters.find(({ id }) => id === 'owner.id') as InFilter
  const valueFilter = filters.find(({ id }) => id === 'value') as RangeFilter
  const archivedFilter = filters.find(({ id }) => id === 'archived')

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={dateFilter?.value[0] ? moment(dateFilter.value[0]) : null}
          endDate={dateFilter?.value[1] ? moment(dateFilter.value[1]) : null}
          onConfirm={(startDate, endDate) => {
            handleRangeFilterChange({
              id: 'created',
              start: startDate ? startDate.set('hour', 0).unix() * 1000 : null,
              end: endDate ? endDate.set('hour', 23).unix() * 1000 : null,
              filters,
              onChange
            })
          }}
          PopperProps={{
            placement: 'bottom-end'
          }}
        />
      </Grid>
      <Grid item xs={12} lg={permissionToViewEstimator ? 4 : 8}>
        <DropdownSelect
          label={`${invoice ? 'Invoice' : quoteLabel.value} Type`}
          variant="multi"
          options={typeOptions}
          searchable
          hideCloseButton
          placeholder={{
            selectPlaceholder: `Any ${invoice ? 'invoice' : quoteLabel.value.toLowerCase()} type`,
            plural: `Types`
          }}
          value={typeOptions.filter((opt) =>
            (filters.find((filter) => filter.id === 'quoteType') as InFilter)?.value.find((v) => v === opt.value)
          )}
          onChange={(selectedOptions) => {
            handleFilterChange(
              {
                id: 'quoteType',
                type: 'in',
                value: selectedOptions.map((o) => o.value)
              },
              filters,
              onChange
            )
          }}
        />
      </Grid>
      {permissionToViewEstimator && (
        <Grid item xs={12} lg={4}>
          <EstimatorSelect
            name="owner"
            label="Owner"
            searchable
            variant="multi"
            value={(typeof ownerFilter?.value === 'string' ? [ownerFilter?.value] : ownerFilter?.value)?.map((val) =>
              addAuth0Prefix(val as string)
            )}
            roles={['sales', 'admin', 'viewer']}
            placeholder={{
              selectPlaceholder: 'Any owner',
              plural: 'Owners'
            }}
            onChange={(options: DropdownSelectOption[]) => {
              handleFilterChange(
                {
                  id: 'owner.id',
                  type: 'in',
                  value: options.map((option) => removeAuth0Prefix(option.value))
                },
                filters,
                onChange
              )
            }}
            fullWidth
          />
        </Grid>
      )}
      <Grid container alignItems="flex-end" item xs={12} lg={4}>
        <Grid item xs={6} style={{ paddingRight: 4 }}>
          <KeypadInputField
            label={`Amount (${currency.symbol})`}
            margin="none"
            placeholder="Min"
            format="dimension"
            fullWidth
            allowClear
            value={valueFilter?.value?.[0] ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: 'value', start: value, filters, onChange })
            }}
          />
        </Grid>
        <Grid item xs={6} style={{ paddingLeft: 4 }}>
          <KeypadInputField
            margin="none"
            flipX
            placeholder="Max"
            format="dimension"
            value={valueFilter?.value?.[1] ?? ''}
            fullWidth
            allowClear
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              handleRangeFilterChange({ id: 'value', end: value, filters, onChange })
            }}
          />
        </Grid>
      </Grid>
      {hasStage && (
        <Grid item xs={12} lg={4}>
          <DropdownSelect
            label={`Stage`}
            variant="multi"
            options={stageOptions}
            searchable
            placeholder={{
              selectPlaceholder: `Any stage`,
              plural: `Stages`
            }}
            value={stageOptions.filter((opt) =>
              (filters.find((filter) => filter.id === 'stage.value') as InFilter)?.value.find((v) => v === opt.value)
            )}
            onChange={(selectedOptions) => {
              handleFilterChange(
                {
                  id: 'stage.value',
                  type: 'in',
                  value: selectedOptions.map((o) => o.value)
                },
                filters,
                onChange
              )
            }}
          />
        </Grid>
      )}
      <Grid item xs={12} lg={4}>
        <TagSelection label="Tags" appliedTags={filteredTags} fullWidth handleChange={handleTagsChange} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <DropdownSelect
          onChange={(option: DropdownSelectOption) => {
            handleFilterChange(
              {
                id: 'archived',
                type: 'in',
                value: option.value
              },
              filters,
              onChange
            )
          }}
          label={'Archived'}
          fullWidth
          variant="single"
          value={
            archivedOptions.find((option) => {
              if (!archivedFilter) {
                return false
              }

              return option.value[0] === archivedFilter?.value[0] && option.value[1] === archivedFilter?.value[1]
            }) || null
          }
          options={archivedOptions}
        />
      </Grid>
      {view === 'board' && (
        <>
          <Grid item xs={hasStage ? 4 : 6}>
            <DropdownSelect
              label={`Sort By`}
              variant="single"
              options={[
                { value: 'ascending', label: 'Ascending' },
                { value: 'descending', label: 'Descending' }
              ]}
              value={
                sort?.startsWith('-')
                  ? { value: 'descending', label: 'Descending' }
                  : { value: 'ascending', label: 'Ascending' }
              }
              onChange={() => handleSort({ swapDirection: true })}
            />
          </Grid>
          <Grid item xs={hasStage ? 4 : 6}>
            <DropdownSelect
              label={`Order`}
              variant="single"
              options={sortOptions}
              value={sortOptions.find((option) => option.value === sort?.replace('-', ''))}
              onChange={(option) => {
                handleSort({ name: option.value })
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  )
}
